import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewRef, ChangeDetectionStrategy, signal } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { Options } from 'ngx-slider-v2';
import { AloAocStateService } from "../../../core-module/services/state/alo-aoc-state.service";
import { CheckboxStateService } from "../../../core-module/services/state/checkbox-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { InputErrorsStateService } from "../../../core-module/services/state/input-errors-state.service";
@Component({
  standalone: false,
  selector: 'app-lsi',
  templateUrl: './lsi.component.html',
  styleUrls: ['./lsi.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LsiComponent extends FieldType implements OnInit {
  // Feature enable state
  isEnableFeature: boolean;
  private readonly lsiDisabledSignal = signal(false);
  showEnableFeature = false;
  column: LegacyAny;

  // Mode dropdown
  selectedMode: string;
  modeOptions: string[];

  // Polarity radio
  selectedPolarity: string;
  polarityOptions: string[];

  // Active dim level
  activeDimLevel: number;
  activeDimLevelMin: number;
  activeDimLevelMax: number;
  activeDimLevelOptions: Options = {};
  activeDimLevelDisplayValue: LegacyAny;
  activeDimAllowDecimal = false;

  // Inactive dim level
  inactiveDimLevel: number;
  inactiveDimLevelMin: number;
  inactiveDimLevelMax: number;
  inactiveDimLevelOptions: Options = {};
  inactiveDimLevelDisplayValue: LegacyAny;
  inactiveDimAllowDecimal = false;

  // Fade on time
  fadeOnTime: number;
  fadeOnTimeMin: number;
  fadeOnTimeMax: number;
  fadeOnTimeOptions: Options = {};
  fadeOnTimeDisplayValue: LegacyAny;

  // Hold off delay
  holdOffDelay: number;
  holdOffDelayMin: number;
  holdOffDelayMax: number;
  holdOffDelayOptions: Options = {};
  holdOffDelayDisplayValue: LegacyAny;

  // Fade off time
  fadeOffTime: number;
  fadeOffTimeMin: number;
  fadeOffTimeMax: number;
  fadeOffTimeOptions: Options = {};
  fadeOffTimeDisplayValue: LegacyAny;

  // Switch off delay
  switchOffDelay: number;
  switchOffDelayMin: number;
  switchOffDelayMax: number;
  switchOffDelayOptions: Options = {};
  switchOffDelayDisplayValue: LegacyAny;

  // Enable switch off delay checkbox
  enableSwitchOffDelay: boolean;

  // Default values from schema
  private isEnableFeatureDefault: boolean;
  private selectedModeDefault: string;
  private selectedPolarityDefault: string;
  private activeDimLevelDefault: number;
  private inactiveDimLevelDefault: number;
  private fadeOnTimeDefault: number;
  private holdOffDelayDefault: number;
  private fadeOffTimeDefault: number;
  private switchOffDelayDefault: number;
  private enableSwitchOffDelayDefault: boolean;

  // Error tracking
  inputError: LegacyAny = {};
  private readonly lsiDisabledButtonSignal = signal(false);

  // Default color tracking
  private readonly defaultColorSignal = signal(true);

  // Standard/Advanced mode
  private readonly quickConfigvalueSignal = signal(false);
  quickConfigCustomCSS: string;
  setQuickConfig: string;

  // Include in configuration tracking
  private readonly isIncludedInConfigurationSignal = signal(false);
  private ignoreParentUpdate = false;
  private hasConfigData = false;
  private isResetting = false;

  // Input subjects for debouncing
  private readonly activeDimInputSubject = new Subject<string>();
  private readonly inactiveDimInputSubject = new Subject<string>();
  private readonly fadeOnInputSubject = new Subject<string>();
  private readonly holdOffInputSubject = new Subject<string>();
  private readonly fadeOffInputSubject = new Subject<string>();
  private readonly switchOffInputSubject = new Subject<string>();

  private properties: LegacyAny;
  private readonly destroy$ = new Subject<void>();

  get lsiDisabled(): boolean { return this.lsiDisabledSignal(); }
  set lsiDisabled(value: boolean) { this.lsiDisabledSignal.set(!!value); }

  get lsiDisabledButton(): boolean { return this.lsiDisabledButtonSignal(); }
  set lsiDisabledButton(value: boolean) { this.lsiDisabledButtonSignal.set(!!value); }

  get defaultColor(): boolean { return this.defaultColorSignal(); }
  set defaultColor(value: boolean) { this.defaultColorSignal.set(!!value); }

  get quickConfigvalue(): boolean { return this.quickConfigvalueSignal(); }
  set quickConfigvalue(value: boolean) { this.quickConfigvalueSignal.set(!!value); }

  get isIncludedInConfiguration(): boolean { return this.isIncludedInConfigurationSignal(); }
  set isIncludedInConfiguration(value: boolean) { this.isIncludedInConfigurationSignal.set(!!value); }

  constructor(
    
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private checkboxState: CheckboxStateService,
    private inputErrors: InputErrorsStateService,
    private aloAoc: AloAocStateService
  ) {
    super();
  }

  ngOnInit(): void {
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    // Get schema properties if available
    this.properties = (this.field as LegacyAny)?.properties;

    // Check if Enabled exists in required properties
    this.column = (this.field as LegacyAny).requiredProperties;
    this.showEnableFeature = this.column?.includes('Enabled') ?? false;

    // Load from schema first to initialize all values
    if (this.properties) {
      this.loadFromSchema();
    }

    // If enable feature checkbox is not shown, ensure fields are not disabled
    if (!this.showEnableFeature) {
      this.lsiDisabled = false;
    }

    // Initialize slider options
    this.initializeSliderOptions();

    // Initialize display values
    this.activeDimLevelDisplayValue = this.activeDimLevel;
    this.inactiveDimLevelDisplayValue = this.inactiveDimLevel;
    this.fadeOnTimeDisplayValue = this.fadeOnTime;
    this.holdOffDelayDisplayValue = this.holdOffDelay;
    this.fadeOffTimeDisplayValue = this.fadeOffTime;
    this.switchOffDelayDisplayValue = this.switchOffDelay;

    // Load saved configuration data (matching dali102and207 pattern)
    const configData = this.appStateService.getConfigurationData();
    let savedValue = null;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element["featureName"] && element["featureName"].toString().toUpperCase() === "LSI") {
          isConfigEdited = true;
          savedValue = element["featureParameters"][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    if (!isConfigEdited) {
      const lsiData = this.appStateService.getFeatureData('LSI');
      if (lsiData) {
        savedValue = lsiData;
        // If data exists in storage, mark as hasConfigData (refresh scenario)
        this.hasConfigData = Object.keys(savedValue).length > 0;
      }
    }

    // Load values from savedValue or formControl (matching dali102and207 pattern)
    const currentValue = savedValue || this.formControl.value || {};
    if (currentValue && Object.keys(currentValue).length > 0) {
      this.loadDataFromObject(currentValue);
    }

    // Setup input debouncing
    this.setupInputDebouncing();

    // Subscribe to restore default trigger
    this.featureDefaults.getLsiDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === "valid") {
          this.defaultColor = true;
          this.resetToDefaults();
          // Reset the BehaviorSubject to prevent replaying "valid" on next subscription
          this.featureDefaults.setLsiDefaultValue("");
        }
      });

    // Subscribe to parent "Include in configuration" checkbox changes (matching dali102 pattern)
    this.checkboxState.getLsiParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          // Handle unchecking: reset state
          if (!state.checked) {
            this.hasConfigData = false;
            this.inputErrors.setLsiInputError.next(false);
            this.ref.detectChanges();
            return;
          }

          // Handle checking: refresh flow
          if (this.hasConfigData) {
            // Refresh flow: preserve current state and send current error state
            this.sendErrorStateToParent();
            return;
          }

          this.updateFormControl();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    // Set initial disabled state based on enable checkbox
    this.enableFeatureChanged(this.isEnableFeature);

    // Revalidate all fields after loading from storage to restore error states (matching dali102 pattern)
    this.revalidateAllFields();

    this.checkDefaults();

    // Enable checkbox if LegacyAny value is non-default
    if (!this.defaultColor) {
      this.checkboxState.enableLsiCheckbox();
      this.isIncludedInConfiguration = true;
      this.hasConfigData = true;
    }

    // Send error state AFTER isIncludedInConfiguration is set (so parent knows about errors on refresh)
    this.sendErrorStateToParent();

    // Subscribe to Standard/Advanced mode changes
    this.aloAoc.getquickConfig
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data === 'quickConfig') {
          this.quickConfigvalue = true;
          this.quickConfigCustomCSS = 'quick-custom-inputbox textBoxUnit';
          this.setQuickConfig = 'quickConfig';
        } else if (data === 'standard') {
          this.quickConfigvalue = false;
          this.quickConfigCustomCSS = 'custom-inputbox';
          this.setQuickConfig = 'standard';
        }
        setTimeout(() => {
          if (this.ref && !(this.ref as ViewRef).destroyed) {
            this.ref.detectChanges();
          }
        });
      });

    // Allow parent updates after initialization
    this.ignoreParentUpdate = false;
  }
  private initializeSliderOptions(): void {
    const emptyTicks: number[] = [];

    this.activeDimLevelOptions = {
      floor: this.activeDimLevelMin,
      ceil: this.activeDimLevelMax,
      disabled: this.lsiDisabled,
      step: this.activeDimAllowDecimal ? 0.1 : 1,
      ticksArray: emptyTicks
    };

    this.inactiveDimLevelOptions = {
      floor: this.inactiveDimLevelMin,
      ceil: this.inactiveDimLevelMax,
      disabled: this.lsiDisabled,
      step: this.inactiveDimAllowDecimal ? 0.1 : 1,
      ticksArray: emptyTicks
    };

    this.fadeOnTimeOptions = {
      floor: this.fadeOnTimeMin,
      ceil: this.fadeOnTimeMax,
      disabled: this.lsiDisabled,
      ticksArray: emptyTicks
    };

    this.holdOffDelayOptions = {
      floor: this.holdOffDelayMin,
      ceil: this.holdOffDelayMax,
      disabled: this.lsiDisabled,
      ticksArray: emptyTicks
    };

    this.fadeOffTimeOptions = {
      floor: this.fadeOffTimeMin,
      ceil: this.fadeOffTimeMax,
      disabled: this.lsiDisabled,
      ticksArray: emptyTicks
    };

    this.switchOffDelayOptions = {
      floor: this.switchOffDelayMin,
      ceil: this.switchOffDelayMax,
      disabled: this.lsiDisabled,
      ticksArray: emptyTicks
    };
  }
  private propDefault<T>(propKey: string, fallback: T): T {
    const prop = this.properties[propKey];
    return (prop && prop.default !== undefined) ? prop.default : fallback;
  }

  private propMin(propKey: string, fallback: number): number {
    return this.properties[propKey]?.minimum ?? fallback;
  }

  private propMax(propKey: string, fallback: number): number {
    return this.properties[propKey]?.maximum ?? fallback;
  }

  private loadFromSchema(): void {
    if (!this.properties) {
     return;
    }

    this.isEnableFeature = this.isEnableFeatureDefault = this.propDefault('Enabled', false);

    this.modeOptions = this.properties.Mode?.enum;
    this.selectedMode = this.selectedModeDefault = this.propDefault('Mode', 'Occupancy sensor');

    this.polarityOptions = this.properties.Polarity?.enum;
    this.selectedPolarity = this.selectedPolarityDefault = this.propDefault('Polarity', 'Active when LSI input is high');

    this.activeDimLevelMin = this.propMin('ActiveDimLevel', 0);
    this.activeDimLevelMax = this.propMax('ActiveDimLevel', 100);
    this.activeDimLevel = this.activeDimLevelDefault = this.propDefault('ActiveDimLevel', 100);
    this.activeDimAllowDecimal = this.properties.ActiveDimLevel?.type === 'number';

    this.inactiveDimLevelMin = this.propMin('InactiveDimLevel', 0);
    this.inactiveDimLevelMax = this.propMax('InactiveDimLevel', 100);
    this.inactiveDimLevel = this.inactiveDimLevelDefault = this.propDefault('InactiveDimLevel', 50);
    this.inactiveDimAllowDecimal = this.properties.InactiveDimLevel?.type === 'number';

    this.fadeOnTimeMin = this.propMin('FadeOnTime', 0);
    this.fadeOnTimeMax = this.propMax('FadeOnTime', 90);
    this.fadeOnTime = this.fadeOnTimeDefault = this.propDefault('FadeOnTime', 0);

    this.holdOffDelayMin = this.propMin('HoldOffDelay', 0);
    this.holdOffDelayMax = this.propMax('HoldOffDelay', 64800);
    this.holdOffDelay = this.holdOffDelayDefault = this.propDefault('HoldOffDelay', 0);

    this.fadeOffTimeMin = this.propMin('FadeOffTime', 0);
    this.fadeOffTimeMax = this.propMax('FadeOffTime', 90);
    this.fadeOffTime = this.fadeOffTimeDefault = this.propDefault('FadeOffTime', 0);

    this.switchOffDelayMin = this.propMin('SwitchOffDelay', 0);
    this.switchOffDelayMax = this.propMax('SwitchOffDelay', 64800);
    this.switchOffDelay = this.switchOffDelayDefault = this.propDefault('SwitchOffDelay', 0);

    this.enableSwitchOffDelay = this.enableSwitchOffDelayDefault = this.propDefault('EnableOutputOff', false);

    this.lsiDisabled = !this.isEnableFeature;
  }
  private loadDataFromObject(data: LegacyAny): void {
    // Check for both IsEnabled and Enabled (schema uses Enabled)
    if (data.IsEnabled !== undefined) {
      this.isEnableFeature = data.IsEnabled;
    } else if (data.Enabled !== undefined) {
      this.isEnableFeature = data.Enabled;
    }
    if (data.Mode !== undefined) {
      this.selectedMode = data.Mode;
    }
    if (data.Polarity !== undefined) {
      this.selectedPolarity = data.Polarity;
    }
    if (data.ActiveDimLevel !== undefined) {
      this.activeDimLevel = this.activeDimLevelDisplayValue = data.ActiveDimLevel;
    }
    if (data.InactiveDimLevel !== undefined) {
      this.inactiveDimLevel = this.inactiveDimLevelDisplayValue = data.InactiveDimLevel;
    }
    if (data.FadeOnTime !== undefined) {
      this.fadeOnTime = this.fadeOnTimeDisplayValue = data.FadeOnTime;
    }
    if (data.HoldOffDelay !== undefined) {
      this.holdOffDelay = this.holdOffDelayDisplayValue = data.HoldOffDelay;
    }
    if (data.FadeOffTime !== undefined) {
      this.fadeOffTime = this.fadeOffTimeDisplayValue = data.FadeOffTime;
    }
    if (data.SwitchOffDelay !== undefined) {
      this.switchOffDelay = this.switchOffDelayDisplayValue = data.SwitchOffDelay;
    }
    // Check for both EnableSwitchOffDelay and EnableOutputOff (schema uses EnableOutputOff)
    if (data.EnableSwitchOffDelay !== undefined) {
      this.enableSwitchOffDelay = data.EnableSwitchOffDelay;
    } else if (data.EnableOutputOff !== undefined) {
      this.enableSwitchOffDelay = data.EnableOutputOff;
    }
  }
  private setupInputDebouncing(): void {
    this.activeDimInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'activeDim');
    });
    this.inactiveDimInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'inactiveDim');
    });
    this.fadeOnInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'fadeOn');
    });
    this.holdOffInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'holdOff');
    });
    this.fadeOffInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'fadeOff');
    });
    this.switchOffInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'switchOff');
    });
  }
  // Enable/Disable feature checkbox handler
  enableFeatureChanged(checked: boolean): void {
    this.ignoreParentUpdate = true;
    this.isEnableFeature = checked;
    this.lsiDisabled = !checked;

    // Update all slider options with disabled state
    this.activeDimLevelOptions = Object.assign({}, this.activeDimLevelOptions, { disabled: this.lsiDisabled });
    this.inactiveDimLevelOptions = Object.assign({}, this.inactiveDimLevelOptions, { disabled: this.lsiDisabled });
    this.fadeOnTimeOptions = Object.assign({}, this.fadeOnTimeOptions, { disabled: this.lsiDisabled });
    this.holdOffDelayOptions = Object.assign({}, this.holdOffDelayOptions, { disabled: this.lsiDisabled });
    this.fadeOffTimeOptions = Object.assign({}, this.fadeOffTimeOptions, { disabled: this.lsiDisabled });
    this.switchOffDelayOptions = Object.assign({}, this.switchOffDelayOptions, { disabled: this.lsiDisabled });

    this.checkDefaults();

    // Auto-enable "Include in configuration" when user makes a change
    if (!this.defaultColor) {
      this.checkboxState.enableLsiCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }
  // Slider change handlers
  activeDimLevelChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['activeDim'] = false;
    if (value >= this.activeDimLevelMin && value <= this.activeDimLevelMax) {
      this.activeDimLevel = value;
      this.activeDimLevelDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      // Auto-enable "Include in configuration" when user makes a change
      if (!this.defaultColor) {
        this.checkboxState.enableLsiCheckbox();
        this.isIncludedInConfiguration = true;
      }

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }
  inactiveDimLevelChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['inactiveDim'] = false;
    if (value >= this.inactiveDimLevelMin && value <= this.inactiveDimLevelMax) {
      this.inactiveDimLevel = value;
      this.inactiveDimLevelDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      // Auto-enable "Include in configuration" when user makes a change
      if (!this.defaultColor) {
        this.checkboxState.enableLsiCheckbox();
        this.isIncludedInConfiguration = true;
      }

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }
  fadeOnTimeChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['fadeOn'] = false;
    if (value >= this.fadeOnTimeMin && value <= this.fadeOnTimeMax) {
      this.fadeOnTime = value;
      this.fadeOnTimeDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      // Auto-enable "Include in configuration" when user makes a change
      if (!this.defaultColor) {
        this.checkboxState.enableLsiCheckbox();
        this.isIncludedInConfiguration = true;
      }

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }
  holdOffDelayChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['holdOff'] = false;
    if (value >= this.holdOffDelayMin && value <= this.holdOffDelayMax) {
      this.holdOffDelay = value;
      this.holdOffDelayDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      // Auto-enable "Include in configuration" when user makes a change
      if (!this.defaultColor) {
        this.checkboxState.enableLsiCheckbox();
        this.isIncludedInConfiguration = true;
      }

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }
  fadeOffTimeChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['fadeOff'] = false;
    if (value >= this.fadeOffTimeMin && value <= this.fadeOffTimeMax) {
      this.fadeOffTime = value;
      this.fadeOffTimeDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      // Auto-enable "Include in configuration" when user makes a change
      if (!this.defaultColor) {
        this.checkboxState.enableLsiCheckbox();
        this.isIncludedInConfiguration = true;
      }

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }
  switchOffDelayChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['switchOff'] = false;
    if (value >= this.switchOffDelayMin && value <= this.switchOffDelayMax) {
      this.switchOffDelay = value;
      this.switchOffDelayDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      // Auto-enable "Include in configuration" when user makes a change
      if (!this.defaultColor) {
        this.checkboxState.enableLsiCheckbox();
        this.isIncludedInConfiguration = true;
      }

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }
  // Input field change handlers (with debouncing)
  onFieldInput(value: string, type: string): void {
    switch (type) {
      case 'activeDim':
        this.activeDimInputSubject.next(value);
        break;
      case 'inactiveDim':
        this.inactiveDimInputSubject.next(value);
        break;
      case 'fadeOn':
        this.fadeOnInputSubject.next(value);
        break;
      case 'holdOff':
        this.holdOffInputSubject.next(value);
        break;
      case 'fadeOff':
        this.fadeOffInputSubject.next(value);
        break;
      case 'switchOff':
        this.switchOffInputSubject.next(value);
        break;
    }
  }
  // Validate and update field value
  private validateAndUpdateField(value: string, type: string): void {
    // Skip if a reset is in progress (pending debounced values should be discarded)
    if (this.isResetting) {
      return;
    }

    this.ignoreParentUpdate = true;
    const numValue = ((type === 'activeDim' && this.activeDimAllowDecimal) || (type === 'inactiveDim' && this.inactiveDimAllowDecimal))
      ? parseFloat(value)
      : parseInt(value, 10);
    let min: number, max: number;

    switch (type) {
      case 'activeDim':
        min = this.activeDimLevelMin;
        max = this.activeDimLevelMax;
        break;
      case 'inactiveDim':
        min = this.inactiveDimLevelMin;
        max = this.inactiveDimLevelMax;
        break;
      case 'fadeOn':
        min = this.fadeOnTimeMin;
        max = this.fadeOnTimeMax;
        break;
      case 'holdOff':
        min = this.holdOffDelayMin;
        max = this.holdOffDelayMax;
        break;
      case 'fadeOff':
        min = this.fadeOffTimeMin;
        max = this.fadeOffTimeMax;
        break;
      case 'switchOff':
        min = this.switchOffDelayMin;
        max = this.switchOffDelayMax;
        break;
      default:
        return;
    }

    // Always update internal value (even if invalid)
    // Update corresponding slider value, internal value, and display value
    switch (type) {
      case 'activeDim':
        this.activeDimLevel = numValue;
        this.activeDimLevelDisplayValue = numValue;
        break;
      case 'inactiveDim':
        this.inactiveDimLevel = numValue;
        this.inactiveDimLevelDisplayValue = numValue;
        break;
      case 'fadeOn':
        this.fadeOnTime = numValue;
        this.fadeOnTimeDisplayValue = numValue;
        break;
      case 'holdOff':
        this.holdOffDelay = numValue;
        this.holdOffDelayDisplayValue = numValue;
        break;
      case 'fadeOff':
        this.fadeOffTime = numValue;
        this.fadeOffTimeDisplayValue = numValue;
        break;
      case 'switchOff':
        this.switchOffDelay = numValue;
        this.switchOffDelayDisplayValue = numValue;
        break;
    }

    // Validate input and set error flags
    if (value === '' || value === '-0' || numValue < min || numValue > max || isNaN(numValue)) {
      this.inputError[type] = true;
    } else {
      this.inputError[type] = false;
    }

    // Always auto-enable "Include in configuration" when LegacyAny value changes (matching dali102and207 pattern)
    // NaN !== default is always true, so even invalid/empty values trigger auto-check
    this.checkDefaults();
    if (!this.defaultColor) {
      this.checkboxState.enableLsiCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.recalcErrorState();
    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }
  // Mode dropdown change
  onModeChange(value: string): void {
    this.ignoreParentUpdate = true;
    this.selectedMode = value;
    this.checkDefaults();

    // Auto-enable "Include in configuration" when user makes a change
    if (!this.defaultColor) {
      this.checkboxState.enableLsiCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ignoreParentUpdate = false;
  }
  // Polarity radio change
  onPolarityChange(value: string): void {
    this.ignoreParentUpdate = true;
    this.selectedPolarity = value;
    this.checkDefaults();

    // Auto-enable "Include in configuration" when user makes a change
    if (!this.defaultColor) {
      this.checkboxState.enableLsiCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ignoreParentUpdate = false;
  }
  // Enable switch off delay checkbox change
  onEnableSwitchOffDelayChange(checked: boolean): void {
    this.ignoreParentUpdate = true;
    this.enableSwitchOffDelay = checked;
    this.checkDefaults();

    // Auto-enable "Include in configuration" when user makes a change
    if (!this.defaultColor) {
      this.checkboxState.enableLsiCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ignoreParentUpdate = false;
  }
  // Send error state to parent (matching dali102's sendErrorStateToParent pattern)
  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.inputErrors.setLsiInputError.next(this.lsiDisabledButton);
    } else {
      this.inputErrors.setLsiInputError.next(false);
    }
  }

  // Validate a single field and update its error flag
  private validateField(type: string, value: number, min: number, max: number): void {
    // null means NaN was saved to storage (JSON converts NaN to null)
    if (value === null || value === undefined || isNaN(value) || value < min || value > max) {
      this.inputError[type] = true;
      this.lsiDisabledButton = true;
    }
  }

  // Revalidate all fields after loading from storage to restore error states (matching dali102 pattern)
  private revalidateAllFields(): void {
    this.lsiDisabledButton = false;
    this.inputError = {};

    this.validateField('activeDim', this.activeDimLevel, this.activeDimLevelMin, this.activeDimLevelMax);
    this.validateField('inactiveDim', this.inactiveDimLevel, this.inactiveDimLevelMin, this.inactiveDimLevelMax);
    this.validateField('fadeOn', this.fadeOnTime, this.fadeOnTimeMin, this.fadeOnTimeMax);
    this.validateField('holdOff', this.holdOffDelay, this.holdOffDelayMin, this.holdOffDelayMax);
    this.validateField('fadeOff', this.fadeOffTime, this.fadeOffTimeMin, this.fadeOffTimeMax);
    this.validateField('switchOff', this.switchOffDelay, this.switchOffDelayMin, this.switchOffDelayMax);

    // Send error state to parent
    this.sendErrorStateToParent();
  }

  // Recalculate lsiDisabledButton from inputError flags
  private recalcErrorState(): void {
    this.lsiDisabledButton = false;
    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.lsiDisabledButton = true;
        break;
      }
    }
  }
  // Check if values match defaults
  checkDefaults(): void {
    this.defaultColor = (
      this.isEnableFeature === this.isEnableFeatureDefault &&
      this.selectedMode === this.selectedModeDefault &&
      this.selectedPolarity === this.selectedPolarityDefault &&
      this.activeDimLevel === this.activeDimLevelDefault &&
      this.inactiveDimLevel === this.inactiveDimLevelDefault &&
      this.fadeOnTime === this.fadeOnTimeDefault &&
      this.holdOffDelay === this.holdOffDelayDefault &&
      this.fadeOffTime === this.fadeOffTimeDefault &&
      this.switchOffDelay === this.switchOffDelayDefault &&
      this.enableSwitchOffDelay === this.enableSwitchOffDelayDefault
    );
    this.featureDefaults.setLsiDefaultColor(this.defaultColor);
  }
  // Reset to defaults (matching dali102 pattern)
  resetToDefaults(): void {
    // Prevent LegacyAny pending debounced input values from overriding the reset
    this.isResetting = true;

    // Preserve checkbox state before resetting
    const wasIncludedInConfiguration = this.isIncludedInConfiguration;

    this.isEnableFeature = this.isEnableFeatureDefault;
    this.selectedMode = this.selectedModeDefault;
    this.selectedPolarity = this.selectedPolarityDefault;
    this.activeDimLevel = this.activeDimLevelDisplayValue = this.activeDimLevelDefault;
    this.inactiveDimLevel = this.inactiveDimLevelDisplayValue = this.inactiveDimLevelDefault;
    this.fadeOnTime = this.fadeOnTimeDisplayValue = this.fadeOnTimeDefault;
    this.holdOffDelay = this.holdOffDelayDisplayValue = this.holdOffDelayDefault;
    this.fadeOffTime = this.fadeOffTimeDisplayValue = this.fadeOffTimeDefault;
    this.switchOffDelay = this.switchOffDelayDisplayValue = this.switchOffDelayDefault;
    this.enableSwitchOffDelay = this.enableSwitchOffDelayDefault;
    this.lsiDisabled = !this.isEnableFeatureDefault;
    this.inputError = {};
    this.lsiDisabledButton = false;
    this.defaultColor = true;

    // Update slider options after reset
    this.initializeSliderOptions();

    this.updateFormControl();
    this.featureDefaults.setLsiDefaultColor(this.defaultColor);

    // Clear error state when resetting to defaults
    this.inputErrors.setLsiInputError.next(false);

    // Restore checkbox state - if it was checked, keep it checked
    if (wasIncludedInConfiguration) {
      this.isIncludedInConfiguration = true;
      this.checkboxState.enableLsiCheckbox();
    }

    this.ref.detectChanges();

    // Release resetting flag after debounce period to discard LegacyAny pending values
    setTimeout(() => {
      this.isResetting = false;
    }, 150);
  }
  // Update form control with all values
  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }
    const value = {
      Enabled: this.isEnableFeature,
      Mode: this.selectedMode,
      Polarity: this.selectedPolarity,
      ActiveDimLevel: this.activeDimLevel,
      InactiveDimLevel: this.inactiveDimLevel,
      FadeOnTime: this.fadeOnTime,
      HoldOffDelay: this.holdOffDelay,
      FadeOffTime: this.fadeOffTime,
      SwitchOffDelay: this.switchOffDelay,
      EnableOutputOff: this.enableSwitchOffDelay
    };
    this.formControl.setValue(value);
    this.setConfigurationData(value, 'LSI');
  }

  private setConfigurationData(value: LegacyAny, keyVal: string): void {
    if (value != null) {
      // Use centralized method to save feature data
      // This automatically saves to both ConfigurationData and SaveSetData
      const lsiObj = Object.assign({}, value);
      this.appStateService.setFeatureData(keyVal, lsiObj);
    }
  }

  // Safe value for slider one-way binding (prevents slider from receiving null/NaN/out-of-range)
  getSliderSafeValue(value: number, min: number, max: number): number {
    if (value === null || value === undefined || isNaN(value)){
      return min;
    }
    if (value < min){
      return min;
    }
    if (value > max){
      return max;
    }
    return value;
  }

  // Normalize input value on blur (e.g. "34." †’ "34", force DOM update)
  onFieldBlur(inputEl: HTMLInputElement, type: string): void {
    const raw = inputEl.value;
    const numericValue = parseFloat(raw);
    const cleaned = isNaN(numericValue) ? '' : String(numericValue);
    // Always force DOM update by clearing first, then setting the clean value
    inputEl.value = '';
    inputEl.value = cleaned;
    if (type === 'activeDim') {
      this.activeDimLevelDisplayValue = isNaN(numericValue) ? '' : numericValue;
    } else if (type === 'inactiveDim') {
      this.inactiveDimLevelDisplayValue = isNaN(numericValue) ? '' : numericValue;
    }
  }

  // Keypress validation: allows digits only, or digits + decimal point for decimal fields
  validateKeypress(event, allowDecimal: boolean): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    // Allow '.' (charCode 46) for decimal fields
    if (charCode === 46 && allowDecimal){
      return true;
    }
    if (charCode > 31 && (charCode < 48 || charCode > 57)){
     return false;
    }
    return true;
  }

  // Number only validation for input fields
  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
